import { z } from "zod";
import { nativeToolNamesWhere } from "./native-tool-contracts.js";
export const accessPrincipalKindSchema = z.enum([
    "human",
    "service",
    "workload",
    "operator",
    "system",
]);
export const accessPolicyModeSchema = z.enum([
    "legacy",
    "shadow",
    "warn",
    "enforce",
]);
export const accessPrincipalSchema = z.object({
    kind: accessPrincipalKindSchema,
    id: z.string().trim().min(1).max(300),
}).strict();
export const accessResourceSchema = z.object({
    kind: z.string().trim().min(1).max(120),
    id: z.string().trim().min(1).max(500),
}).strict();
export const accessRevisionVectorSchema = z.record(z.string().trim().min(1).max(120), z.number().int().nonnegative());
export const accessDecisionOutcomeSchema = z.enum([
    "allow",
    "deny",
    "approval_required",
    "egress_confirmation_required",
]);
export const executionGrantedConnectorEntitySchema = z.object({
    connectorIntegrationId: z.string().trim().min(1).max(300),
    provider: z.string().trim().min(1).max(120),
    entity: z.string().trim().min(1).max(400),
    sourceEntity: z.string().trim().min(1).max(160),
    relationSchema: z.string().regex(/^(?:[a-z][a-z0-9-]{4,61}[a-z0-9]\.)?[A-Za-z_][A-Za-z0-9_$]*$/u),
    relationName: z.string().regex(/^[A-Za-z_][A-Za-z0-9_$]*$/u),
    contractHash: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict().refine((value) => ["postgres", "azure-sql", "bigquery"].includes(value.provider)
    ? value.entity === `${value.relationSchema}.${value.relationName}` && value.sourceEntity === value.relationName
        && value.relationSchema.includes(".") === (value.provider === "bigquery")
    : /^[a-z][a-z0-9_]*$/u.test(value.entity) && !value.relationSchema.includes("."), {
    message: "Postgres entities must match their qualified relation; projected entities must be bare names",
    path: ["entity"],
});
export const MAX_EXECUTION_GRANT_DATA_ENTITIES = 1000;
/**
 * The channel a run works in alone: a run in an external channel, where
 * guests read what the Twin says. It reads and writes that channel only;
 * `records: "read"` (a View guest's run) also keeps it from changing records.
 */
export const runChannelScopeSchema = z.object({
    channelId: z.string().trim().min(1).max(300),
    records: z.enum(["read", "write"]),
    source: z.enum(["external_audience", "slack_binding"]).optional(),
}).strict();
export const accessExecutionGrantV1Schema = z.object({
    version: z.literal(1).default(1),
    tokenId: z.string().trim().min(1).max(200),
    tenantSlug: z.string().trim().min(1).max(200),
    organisationId: z.string().trim().min(1).max(300),
    principal: accessPrincipalSchema,
    actor: accessPrincipalSchema.optional(),
    credentialSubjectId: z.string().trim().min(1).max(300).optional(),
    sessionId: z.string().trim().min(1).max(300),
    runId: z.string().trim().min(1).max(300),
    workerId: z.string().trim().min(1).max(300),
    attemptNumber: z.number().int().positive(),
    audience: z.string().trim().min(1).max(120),
    policyRevisionVector: accessRevisionVectorSchema,
    catalogueRevision: z.number().int().nonnegative(),
    dataEntities: z.array(executionGrantedConnectorEntitySchema)
        .max(MAX_EXECUTION_GRANT_DATA_ENTITIES),
    /** Present only for a run limited to one channel, which then holds no data entities. */
    channelScope: runChannelScopeSchema.optional(),
    taintLabels: z.array(z.enum(["public", "internal", "restricted", "privileged"]))
        .default([]),
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
    kid: z.string().trim().min(1).max(200),
    signature: z.string().trim().min(1),
}).strict().superRefine((grant, refinement) => {
    if (grant.expiresAt <= grant.issuedAt) {
        refinement.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "Grant expiry must be after issuance.",
        });
    }
    if (grant.channelScope && grant.dataEntities.length > 0) {
        refinement.addIssue({
            code: "custom",
            path: ["dataEntities"],
            message: "A run limited to one channel reads no connected data.",
        });
    }
});
const twinQueryGrantedRelationSchema = z.object({
    datasetId: z.string().trim().min(1).max(300),
    schema: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
    relation: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
    product: z.object({
        resourceId: z.string().trim().min(1).max(500),
        publicationId: z.string().trim().min(1).max(300),
        publicationVersionId: z.string().trim().min(1).max(300),
        productSlug: z.string().trim().min(1).max(200),
        productVersion: z.number().int().positive(),
    }).strict().optional(),
}).strict();
export const twinQueryGrantV1Schema = z.object({
    version: z.literal(1).default(1),
    tokenId: z.string().trim().min(1).max(200),
    requestId: z.string().trim().min(1).max(200),
    tenantSlug: z.string().trim().min(1).max(200),
    organisationId: z.string().trim().min(1).max(300),
    principal: accessPrincipalSchema,
    audience: z.literal("warehouse-broker"),
    channelId: z.string().trim().min(1).max(300),
    viewId: z.string().trim().min(1).max(300).optional(),
    objectTypeId: z.string().trim().min(1).max(300).optional(),
    normalizedQueryHash: z.string().regex(/^[a-f0-9]{64}$/u),
    relations: z.array(twinQueryGrantedRelationSchema).max(32),
    dataEntities: z.array(executionGrantedConnectorEntitySchema)
        .max(MAX_EXECUTION_GRANT_DATA_ENTITIES),
    authorizationDecisionIds: z.array(z.string().trim().min(1).max(200)).min(1).max(64),
    policyRevisionVector: accessRevisionVectorSchema,
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
    kid: z.string().trim().min(1).max(200),
    signature: z.string().trim().min(1),
}).strict().superRefine((grant, refinement) => {
    if (grant.expiresAt <= grant.issuedAt) {
        refinement.addIssue({ code: "custom", path: ["expiresAt"],
            message: "Grant expiry must be after issuance." });
    }
});
export const toolClassificationManifestEntryV1Schema = z.object({
    toolName: z.string().trim().min(1).max(300),
    actionId: z.string().trim().min(1).max(200),
    sideEffect: z.enum(["read", "write", "destructive"]),
    egress: z.enum(["none", "internal-share", "external-send"]),
    resourceResolver: z.string().trim().min(1).max(300),
    approval: z.enum(["never", "always", "policy"]),
    argCanonicalization: z.literal("JCS"),
    schemaHash: z.string().regex(/^[a-f0-9]{64}$/u),
    mcpPin: z.object({
        serverId: z.string().trim().min(1),
        toolName: z.string().trim().min(1),
        definitionHash: z.string().regex(/^[a-f0-9]{64}$/u),
    }).strict().optional(),
}).strict();
export const toolClassificationManifestV1Schema = z.object({
    version: z.literal(1).default(1),
    catalogueRevision: z.number().int().nonnegative(),
    entries: z.array(toolClassificationManifestEntryV1Schema),
}).strict();
export const TOOL_CLASSIFICATION_CATALOGUE_VERSION = 1;
/**
 * The executor's native tools with no side effect: they read and write nothing outside the run.
 * The runtime's access guard classifies them as reads (twin-executor/runtime
 * runtime_access_guard.ts), and a run that only ran these (and plugin tools declared `read`) may
 * be retried by the tenant without repeating anything.
 */
export const NATIVE_READ_ONLY_TOOLS = new Set(nativeToolNamesWhere(({ classification }) => classification.sideEffect === "read" && classification.egress === "none"));
export const accessDenialExtensionV1Schema = z.object({
    decisionId: z.string().trim().min(1).max(200),
    outcome: accessDecisionOutcomeSchema.exclude(["allow"]),
    reasonCode: z.string().trim().min(1).max(160),
    // Operational context only. Customer data authority has no action dimension.
    operationId: z.string().trim().min(1).max(200).optional(),
    resourceKind: z.string().trim().min(1).max(120).optional(),
    resourceId: z.string().trim().min(1).max(500).optional(),
    connectorIntegrationId: z.string().trim().min(1).max(300).optional(),
    provider: z.string().trim().min(1).max(120).optional(),
    entity: z.string().trim().min(1).max(160).optional(),
    policyRevision: accessRevisionVectorSchema,
    retryable: z.boolean(),
    remediation: z.string().trim().min(1).max(500).optional(),
}).strict();
export const trustedAuthContextV1Schema = z.object({
    version: z.literal(1).default(1),
    issuer: z.string().trim().min(1),
    audience: z.string().trim().min(1),
    principal: accessPrincipalSchema,
    readableEntities: z.array(executionGrantedConnectorEntitySchema),
    taintLabels: z.array(z.enum(["public", "internal", "restricted", "privileged"])),
    provenanceClass: z.enum(["user", "org-internal", "external-untrusted"]),
    revisionVector: accessRevisionVectorSchema,
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
    kid: z.string().trim().min(1),
    signature: z.string().trim().min(1),
}).strict();
export const approvalDecisionAssertionV1Schema = z.object({
    version: z.literal(1).default(1),
    issuer: z.string().trim().min(1),
    audience: z.string().trim().min(1),
    sessionId: z.string().trim().min(1),
    approvalId: z.string().trim().min(1),
    approverPrincipal: accessPrincipalSchema,
    decision: z.enum(["approve", "reject"]),
    actionId: z.string().trim().min(1),
    resource: accessResourceSchema,
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/u),
    authTime: z.number().int().nonnegative(),
    policyRevisionVector: accessRevisionVectorSchema,
    authorizationDecisionId: z.string().trim().min(1),
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
    jti: z.string().trim().min(1),
    kid: z.string().trim().min(1),
    signature: z.string().trim().min(1),
}).strict();
// Server-to-server membership resolution. The control plane asks admin-control
// whether a principal is currently an active member of an organisation without
// a user token, so run claims and grant renewals can refresh the membership
// projection for principals who never opened an access route themselves.
export const accessMembershipResolutionRequestSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(200),
    organisationId: z.string().trim().min(1).max(300),
    principalId: z.string().trim().min(1).max(300),
}).strict();
export const accessMembershipResolutionSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(200),
    organisationId: z.string().trim().min(1).max(300),
    principalId: z.string().trim().min(1).max(300),
    status: z.enum(["active", "inactive"]),
    organisationAdmin: z.boolean(),
    authority: z.literal("clerk"),
    resolvedAt: z.number().int().nonnegative(),
}).strict();
